// Словарь ролей и разрешений (ключи `access.*`): группы, короткие названия и пояснения для экранов ролей.
// Полноту проверяет каталог `@glory/identity/domain` (access-catalog.ts) на уровне типов. Подключается в `ru.ts`.

/** Каталог ролей и разрешений (ключи `access.*`). */
export const ruAccess = {
  ui: {
    details: 'Что даёт роль',
    can: 'Может',
    cannot: 'Не может',
    combine: 'Права ролей складываются: у сотрудника с двумя ролями — всё, что даёт каждая',
    confirmTitle: 'Сменить роли сотрудника?',
    confirmLead: '{name} получит новые права со следующего действия, открытые экраны обновятся сами',
    gains: 'Получит',
    losses: 'Потеряет',
    nothingChanges: 'Права не меняются',
    confirm: 'Сменить роли',
    cancel: 'Не менять',
    page: {
      nav: 'Роли и доступ',
      title: 'Роли и доступ',
      lead: 'Что может каждая роль. Роли сотрудника меняются в его карточке в «Команде»',
      hint: {
        title: 'Как устроены роли',
        text: 'Роль — готовый набор прав. Права нескольких ролей складываются. Проверка идёт на сервере: то, что роли не положено, не приходит в браузер ни в списках, ни в поиске, ни в уведомлениях',
        example: 'Менеджер видит цены и маржу в сметах, но не личные ставки сотрудников',
      },
      matrix: 'Что может каждая роль',
      permission: 'Право',
      people: 'Кто в роли',
      nobody: 'Пока никого',
      granted: 'Есть',
      denied: 'Нет',
    },
  },
  groups: {
    team: 'Команда',
    pricing: 'Цены',
    sales: 'Продажи',
    estimates: 'Сметы и КП',
    documents: 'Документы',
    production: 'Производство',
    finance: 'Финансы',
    mail: 'Почта',
    messenger: 'Мессенджер',
    system: 'Система',
  },
  roles: {
    admin: {
      title: 'Админ',
      description: 'Управляет студией целиком: команда и роли, ставки, цены, деньги, документы и настройки',
    },
    manager: {
      title: 'Менеджер',
      description:
        'Ведёт клиентов, сделки и проекты, видит цены и маржу, но не личные ставки и данные сотрудников',
    },
    member: {
      title: 'Сотрудник',
      description: 'Работает в своих проектах и сделках, видит свою ставку и каталог без себестоимости',
    },
  },
  permissions: {
    users: {
      invite: { title: 'Приглашать сотрудников', hint: 'Создавать и отзывать приглашения в платформу' },
      manage: {
        title: 'Управлять учётными записями',
        hint: 'Отключать и возвращать сотрудников, сбрасывать их способы входа',
      },
    },
    roles: {
      assign: {
        title: 'Назначать роли',
        hint: 'Менять роли сотрудников — от этого зависит, что им доступно',
      },
    },
    employees: {
      read: {
        title: 'Видеть команду',
        hint: 'Профили коллег: имя, должность, контакты, специализации — без ставок',
      },
      personal: {
        title: 'Личные данные сотрудников',
        hint: 'Паспорт, СНИЛС, банковский счёт и договоры сотрудников. Свои данные каждый видит и без этого',
      },
      hiring: {
        title: 'Вакансии и отклики',
        hint: 'Вести вакансии раздела «Команда» на сайте и разбирать отклики кандидатов; новые отклики приходят уведомлением',
      },
    },
    rates: {
      read: {
        all: { title: 'Ставки всех сотрудников', hint: 'Личные часовые ставки коллег и суммы по ним' },
        own: { title: 'Своя ставка', hint: 'Своя часовая ставка и свои суммы в проектах и актах' },
      },
      manage: { title: 'Менять ставки', hint: 'Назначать и править личные ставки сотрудников' },
    },
    absences: {
      manage: { title: 'Отпуска сотрудников', hint: 'Вносить отпуска и отсутствия за других сотрудников' },
    },
    specializations: {
      read: {
        title: 'Плановые ставки',
        hint: 'Ставки специализаций для оценки работ. Названия специализаций видят все',
      },
      manage: { title: 'Менять специализации', hint: 'Заводить специализации и их плановые ставки' },
    },
    pricing: {
      read: {
        title: 'Себестоимость и маржа',
        hint: 'Политика цены, коэффициенты, налог, себестоимость и маржа в сметах, КП и каталоге',
      },
      manage: { title: 'Менять политику цены', hint: 'Коэффициенты, накладные, налог и минимальная маржа' },
    },
    margin: {
      approve_below_min: {
        title: 'Одобрять низкую маржу',
        hint: 'Отправлять и одобрять КП с маржой ниже минимальной',
      },
    },
    catalog: {
      read: { title: 'Смотреть каталог', hint: 'Услуги и пакеты с клиентскими ценами — без себестоимости' },
      manage: { title: 'Вести каталог', hint: 'Добавлять и менять услуги и пакеты' },
    },
    sales: {
      use: {
        title: 'Продажи',
        hint: 'Заводить лиды и работать в сделках, куда позвали. Лид без права «Вести сделки» уходит менеджерам в очередь',
      },
      lead: {
        title: 'Вести сделки',
        hint: 'Быть ответственным за лид, сделку и клиента; заведённый лид остаётся за вами',
      },
      read_all: {
        title: 'Все продажи',
        hint: 'Все лиды, сделки и клиенты, очередь лидов и назначение ответственных',
      },
      manage: {
        title: 'Настройки продаж',
        hint: 'Причины, таймер очереди, рабочие часы; вернуть выигранную сделку, удалить чужое',
      },
    },
    clients: {
      merge: { title: 'Объединять клиентов', hint: 'Сливать дубли клиентов в одну карточку' },
    },
    proposals: {
      review: { title: 'Утверждать сметы', hint: 'Проверять и утверждать сметы, отправленные на проверку' },
      manage: { title: 'Настройки КП', hint: 'Тарифы, шаблоны оплат, настройки КП и журнал ИИ' },
    },
    cases: {
      manage: { title: 'Вести кейсы', hint: 'Добавлять и менять кейсы, которые показываются в КП' },
    },
    documents: {
      read: {
        title: 'Все документы',
        hint: 'Хранилище документов целиком, а не только документы своих сделок',
      },
      manage: {
        title: 'Готовить документы',
        hint: 'Собирать, отправлять и отзывать договоры, счета и акты, загружать сканы',
      },
      templates: {
        title: 'Шаблоны документов',
        hint: 'Тексты договоров, оферта, нумерация и настройки подписания',
      },
    },
    projects: {
      use: { title: 'Свои проекты', hint: 'Задачи и время в проектах, где вы участник или исполнитель' },
      lead: {
        title: 'Руководить проектами',
        hint: 'Быть руководителем: назначать, принимать работу, подтверждать часы, сдавать этапы',
      },
      read_all: { title: 'Все проекты', hint: 'Видеть любой проект и доску загрузки команды' },
      manage: {
        title: 'Управлять проектами',
        hint: 'Создавать проекты, менять руководителя, архивировать, настройки и начисления подписки',
      },
    },
    vault: {
      read_all: {
        title: 'Видеть все сейфы доступов',
        hint: 'Пароли всех клиентов и проектов, даже без участия в них; каждое «Показать» попадает в журнал',
      },
      manage: {
        title: 'Вести сейф доступов',
        hint: 'Добавлять, править и удалять записи в сейфах клиентов и проектов, где есть доступ',
      },
    },
    finance: {
      read: {
        title: 'Раздел «Финансы»',
        hint: 'Остатки, оплаты, расходы, налоги, поток денег и маржа проектов',
      },
      manage: {
        title: 'Вести финансы',
        hint: 'Записывать оплаты и расходы, загружать выписки, выплаты, счета студии и настройки',
      },
      invoices: {
        title: 'Счета своих клиентов',
        hint: 'Проверять и выставлять черновики счетов своих клиентов, видеть их оплату',
      },
    },
    mail: {
      use: { title: 'Почта', hint: 'Свой рабочий ящик и ящики, к которым дали доступ' },
      manage: { title: 'Управлять почтой', hint: 'Создавать ящики и раздавать к ним доступ' },
    },
    messenger: {
      use: { title: 'Мессенджер', hint: 'Личные чаты, группы и чаты объектов, к которым есть доступ' },
      entity: {
        read_all: {
          title: 'Любой чат объекта',
          hint: 'Открыть чат любой сделки или проекта без приглашения и вступить в него',
        },
      },
      moderate: {
        title: 'Модерировать',
        hint: 'Удалять чужие сообщения в группах и чатах объектов',
      },
      deleted: {
        read: { title: 'Видеть удалённое', hint: 'Читать удалённые сообщения в группах и чатах объектов' },
      },
    },
    studio: {
      manage: { title: 'Настройки студии', hint: 'Реквизиты, подписант, брендинг и общие настройки' },
    },
    audit: {
      read: {
        all: { title: 'Журнал', hint: 'Все действия в платформе: кто, что и когда изменил' },
        entity: {
          title: 'История объектов',
          hint: 'Вкладка «История» в карточках сделок, проектов, каталога и сотрудников, к которым есть доступ',
        },
      },
    },
    calendar: {
      manage: { title: 'Календарь', hint: 'Рабочие и праздничные дни студии' },
    },
  },
} as const;
